import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  FileText,
  Printer,
  Download,
  Search,
  TrendingUp,
  TrendingDown,
  Users,
  Package
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { API, formatCurrency } from "@/lib/utils";

export const CoalLedgerView = () => {
  const [ledgerData, setLedgerData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [totals, setTotals] = useState({
    totalMaterial: 0,
    totalAmount: 0,
    totalPaid: 0,
    totalDue: 0
  });

  const fetchLedgerData = useCallback(async () => {
    setLoading(true);
    const token = localStorage.getItem('session_token');
    const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
    
    try {
      const response = await axios.get(`${API}/coal-ledger-summary`, { headers });
      const data = response.data || [];
      setLedgerData(data);
      
      // Calculate totals
      const calcTotals = data.reduce((acc, party) => ({
        totalMaterial: acc.totalMaterial + (party.total_material_qty || 0),
        totalAmount: acc.totalAmount + (party.total_amount || 0),
        totalPaid: acc.totalPaid + (party.total_paid || 0),
        totalDue: acc.totalDue + (party.total_due || 0)
      }), { totalMaterial: 0, totalAmount: 0, totalPaid: 0, totalDue: 0 });
      
      setTotals(calcTotals);
    } catch (error) {
      console.error("Error fetching ledger:", error);
      toast.error("Failed to load ledger data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLedgerData();
  }, [fetchLedgerData]);

  // Filter data
  const filteredData = ledgerData.filter(party =>
    party.party_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Print ledger
  const printLedger = () => {
    const rows = filteredData.map((party, idx) => `
      <tr>
        <td class="border p-2 text-center">${idx + 1}</td>
        <td class="border p-2 font-medium">${party.party_name}</td>
        <td class="border p-2 text-right">${party.total_material_qty?.toFixed(2) || 0}</td>
        <td class="border p-2 text-right">Rs. ${(party.total_amount || 0).toLocaleString('en-IN')}</td>
        <td class="border p-2 text-right text-green-700">Rs. ${(party.total_paid || 0).toLocaleString('en-IN')}</td>
        <td class="border p-2 text-right ${party.total_due > 0 ? 'text-red-600 font-bold' : 'text-green-600'}">Rs. ${(party.total_due || 0).toLocaleString('en-IN')}</td>
      </tr>
    `).join('');

    const printHTML = `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<title>A1 Coal - Party Ledger Summary</title>
<style>
* { margin: 0; padding: 0; box-sizing: border-box; }
body { font-family: Arial, sans-serif; padding: 20px; font-size: 12px; }
.header { text-align: center; margin-bottom: 20px; padding-bottom: 15px; border-bottom: 2px solid #000; }
.header h1 { font-size: 20px; margin-bottom: 5px; }
.header h2 { font-size: 14px; font-weight: normal; color: #555; }
.date { text-align: right; margin-bottom: 15px; font-size: 11px; color: #666; }
table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
th { background: #f0f0f0; border: 1px solid #000; padding: 8px; font-size: 11px; text-align: center; }
td { border: 1px solid #000; padding: 6px 8px; font-size: 11px; }
.text-right { text-align: right; }
.text-center { text-align: center; }
.font-medium { font-weight: 500; }
.font-bold { font-weight: bold; }
.text-green-700 { color: #15803d; }
.text-green-600 { color: #16a34a; }
.text-red-600 { color: #dc2626; }
.totals-row { background: #f5f5f5; font-weight: bold; }
.summary-cards { display: flex; gap: 15px; margin-bottom: 20px; }
.summary-card { flex: 1; padding: 12px; border: 1px solid #ddd; text-align: center; }
.summary-card .label { font-size: 10px; color: #666; margin-bottom: 5px; }
.summary-card .value { font-size: 14px; font-weight: bold; }
.footer { margin-top: 30px; font-size: 10px; color: #888; text-align: center; border-top: 1px solid #ddd; padding-top: 10px; }
@media print { body { padding: 10px; } }
</style>
</head>
<body>
<div class="header">
  <h1>A1 COAL - PARTY LEDGER</h1>
  <h2>Summary Report</h2>
</div>

<div class="date">Generated: ${new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</div>

<div class="summary-cards">
  <div class="summary-card">
    <div class="label">Total Parties</div>
    <div class="value">${filteredData.length}</div>
  </div>
  <div class="summary-card">
    <div class="label">Total Material (Qty)</div>
    <div class="value">${totals.totalMaterial.toFixed(2)}</div>
  </div>
  <div class="summary-card">
    <div class="label">Total Amount</div>
    <div class="value">Rs. ${totals.totalAmount.toLocaleString('en-IN')}</div>
  </div>
  <div class="summary-card">
    <div class="label">Total Paid</div>
    <div class="value" style="color: #15803d;">Rs. ${totals.totalPaid.toLocaleString('en-IN')}</div>
  </div>
  <div class="summary-card">
    <div class="label">Total Due</div>
    <div class="value" style="color: #dc2626;">Rs. ${totals.totalDue.toLocaleString('en-IN')}</div>
  </div>
</div>

<table>
  <thead>
    <tr>
      <th width="5%">S.No</th>
      <th width="30%">Party Name</th>
      <th width="15%">Total Material (Qty)</th>
      <th width="15%">Total Amount</th>
      <th width="15%">Paid</th>
      <th width="15%">Due</th>
    </tr>
  </thead>
  <tbody>
    ${rows}
    <tr class="totals-row">
      <td colspan="2" class="text-right">GRAND TOTAL:</td>
      <td class="text-right">${totals.totalMaterial.toFixed(2)}</td>
      <td class="text-right">Rs. ${totals.totalAmount.toLocaleString('en-IN')}</td>
      <td class="text-right text-green-700">Rs. ${totals.totalPaid.toLocaleString('en-IN')}</td>
      <td class="text-right text-red-600">Rs. ${totals.totalDue.toLocaleString('en-IN')}</td>
    </tr>
  </tbody>
</table>

<div class="footer">
  A1 Coal Ledger System | Generated on ${new Date().toLocaleString('en-IN')}
</div>
</body>
</html>`;

    const iframe = document.createElement('iframe');
    iframe.style.position = 'absolute';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = 'none';
    iframe.style.left = '-9999px';
    document.body.appendChild(iframe);
    
    const iframeDoc = iframe.contentWindow.document;
    iframeDoc.open();
    iframeDoc.write(printHTML);
    iframeDoc.close();
    
    iframe.onload = () => {
      setTimeout(() => {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
        setTimeout(() => document.body.removeChild(iframe), 1000);
      }, 250);
    };
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="h-24 bg-slate-200 rounded-sm animate-pulse" />
        <div className="h-64 bg-slate-200 rounded-sm animate-pulse" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="p-4 text-center">
            <Users className="w-6 h-6 mx-auto mb-2 text-blue-600" />
            <p className="text-xs text-blue-600 mb-1">Total Parties</p>
            <p className="text-2xl font-bold text-blue-700">{ledgerData.length}</p>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
          <CardContent className="p-4 text-center">
            <Package className="w-6 h-6 mx-auto mb-2 text-amber-600" />
            <p className="text-xs text-amber-600 mb-1">Total Material</p>
            <p className="text-xl font-bold text-amber-700">{totals.totalMaterial.toFixed(2)}</p>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-slate-50 to-slate-100 border-slate-200">
          <CardContent className="p-4 text-center">
            <TrendingUp className="w-6 h-6 mx-auto mb-2 text-slate-600" />
            <p className="text-xs text-slate-600 mb-1">Total Amount</p>
            <p className="text-lg font-bold text-slate-700">{formatCurrency(totals.totalAmount)}</p>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardContent className="p-4 text-center">
            <TrendingUp className="w-6 h-6 mx-auto mb-2 text-green-600" />
            <p className="text-xs text-green-600 mb-1">Total Paid</p>
            <p className="text-lg font-bold text-green-700">{formatCurrency(totals.totalPaid)}</p>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
          <CardContent className="p-4 text-center">
            <TrendingDown className="w-6 h-6 mx-auto mb-2 text-red-600" />
            <p className="text-xs text-red-600 mb-1">Total Due</p>
            <p className="text-lg font-bold text-red-700">{formatCurrency(totals.totalDue)}</p>
          </CardContent>
        </Card>
      </div>

      {/* Ledger Table */}
      <Card className="border border-slate-200 shadow-sm rounded-sm">
        <CardHeader className="border-b border-slate-100 pb-4">
          <div className="flex items-center justify-between">
            <CardTitle className="font-display font-bold text-lg tracking-tight flex items-center gap-2">
              <FileText className="w-5 h-5 text-amber-600" />
              A1 Coal - Party Ledger Summary
            </CardTitle>
            <Button 
              onClick={printLedger}
              className="bg-amber-600 hover:bg-amber-700 text-white rounded-sm"
            >
              <Printer className="w-4 h-4 mr-2" />
              Print Ledger
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-4">
          {/* Search */}
          <div className="flex gap-4 mb-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search party name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 rounded-sm"
              />
            </div>
          </div>

          {/* Table */}
          <div className="border rounded-sm overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-100">
                  <TableHead className="text-xs font-bold w-12">S.No</TableHead>
                  <TableHead className="text-xs font-bold">Party Name</TableHead>
                  <TableHead className="text-xs font-bold text-right">Total Material (Qty)</TableHead>
                  <TableHead className="text-xs font-bold text-right">Total Amount</TableHead>
                  <TableHead className="text-xs font-bold text-right">Paid</TableHead>
                  <TableHead className="text-xs font-bold text-right">Due</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                      No data found
                    </TableCell>
                  </TableRow>
                ) : (
                  <>
                    {filteredData.map((party, idx) => (
                      <TableRow key={party.party_name} className="hover:bg-slate-50">
                        <TableCell className="text-sm text-center">{idx + 1}</TableCell>
                        <TableCell className="text-sm font-medium">{party.party_name}</TableCell>
                        <TableCell className="text-sm text-right font-mono">{party.total_material_qty?.toFixed(2) || 0}</TableCell>
                        <TableCell className="text-sm text-right font-mono">{formatCurrency(party.total_amount || 0)}</TableCell>
                        <TableCell className="text-sm text-right font-mono text-green-600">{formatCurrency(party.total_paid || 0)}</TableCell>
                        <TableCell className={`text-sm text-right font-mono font-semibold ${party.total_due > 0 ? 'text-red-600' : 'text-green-600'}`}>
                          {formatCurrency(party.total_due || 0)}
                        </TableCell>
                      </TableRow>
                    ))}
                    {/* Totals Row */}
                    <TableRow className="bg-slate-100 font-bold">
                      <TableCell colSpan={2} className="text-right text-sm">GRAND TOTAL:</TableCell>
                      <TableCell className="text-sm text-right font-mono">{totals.totalMaterial.toFixed(2)}</TableCell>
                      <TableCell className="text-sm text-right font-mono">{formatCurrency(totals.totalAmount)}</TableCell>
                      <TableCell className="text-sm text-right font-mono text-green-700">{formatCurrency(totals.totalPaid)}</TableCell>
                      <TableCell className="text-sm text-right font-mono text-red-700">{formatCurrency(totals.totalDue)}</TableCell>
                    </TableRow>
                  </>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
